/*
 * Space Syndicate — fin de la baisse affichée des annonces, à l'heure du visiteur (décision 028-baisse-prix-proportionnelle,
 * A3 et A8).
 *
 * Module ES partagé : la première partie (« noyau ») est pure, sans DOM, importée par les tests Node
 * (tools/universe-lab/price-drop.test.mjs) ; la seconde (« interface ») ne s'exécute que dans un navigateur.
 *
 * CONTRAT
 * - Le Liquid (fs-listing-price, mode 'drop') ne rend la baisse que si elle est active au rendu. La page est en cache chez
 *   Shopify : ce module la retire quand sa fin (secondes Unix, data-fs-drop-until) est passée, comme fs-new-badge.js pour
 *   « Nouveau ». Aucun calcul de montant, aucune requête réseau.
 * - Éléments : chaque [data-fs-drop-part] (pastille, prix barré, ligne du prix de référence ; fs-listing-drop) reçoit hidden.
 *   Racine d'une carte [data-fs-drop] (fs-listing-card) : data-fs-drop et data-fs-drop-until retirés (filtre « En baisse » et
 *   face-à-face ne la comptent plus), et « , <data-fs-drop-label> » retiré du nom accessible de [data-fs-listing-link].
 * - Une fin dans les prochaines 24 h est aussi programmée (onglet laissé ouvert).
 * - window.FSDrop : { active(until, opts), scan(root) } ; scan() est idempotent (à rappeler après l'insertion d'annonces
 *   clonées : barre de comparaison, face-à-face).
 * - La zone [data-fs-commerce] n'est touchée que par l'attribut hidden du prix barré : le prix affiché et data-fs-price ne
 *   changent jamais.
 */

/* ============================== Noyau pur ============================== */

/** Pourcentage minimal d'une pastille (REDUCTION_MIN_PERCENT du service, même seuil que fs-listing-price). */
export const DROP_MIN_PERCENT = 1;

/** Fin lue dans un attribut (secondes Unix, entier positif) → millisecondes, sinon null. */
export function untilTime(until) {
  const text = String(until ?? '').trim();
  if (!/^\d{9,11}$/.test(text)) return null;
  return Number(text) * 1000;
}

/** Baisse encore affichée ? Fin illisible : non (jamais de prix barré sans fin connue). */
export function active(until, { now = Date.now() } = {}) {
  const time = untilTime(until);
  return time !== null && time > now;
}

/** Pourcentage lu sur une carte (data-fs-drop) : entier ≥ DROP_MIN_PERCENT et < 100, sinon null. */
export function dropPercent(raw) {
  const text = String(raw ?? '').trim();
  if (!/^\d{1,2}$/.test(text)) return null;
  const value = Number(text);
  return value >= DROP_MIN_PERCENT ? value : null;
}

/** Nom accessible sans la mention de la baisse (« …, en baisse de 17 %, 96 € » → « …, 96 € »). */
export function withoutDropLabel(name, label) {
  if (!name || !label) return name;
  const part = `, ${label}`;
  const at = name.indexOf(part);
  return at === -1 ? name : name.slice(0, at) + name.slice(at + part.length);
}

/* ============================== Interface ============================== */

if (typeof document !== 'undefined' && typeof window !== 'undefined') {
  let timer = null;

  const end = (root) => {
    const label = root.getAttribute('data-fs-drop-label');
    root.removeAttribute('data-fs-drop');
    root.removeAttribute('data-fs-drop-until');
    root.querySelectorAll('[data-fs-listing-link]').forEach((link) => {
      const name = link.getAttribute('aria-label');
      const next = withoutDropLabel(name, label);
      if (next !== name) link.setAttribute('aria-label', next);
    });
    root.removeAttribute('data-fs-drop-label');
  };

  const scan = (scope = document) => {
    const now = Date.now();
    scope.querySelectorAll('[data-fs-drop-until]').forEach((el) => {
      if (active(el.getAttribute('data-fs-drop-until'), { now })) return;
      if (el.hasAttribute('data-fs-drop-part')) el.hidden = true;
      if (el.hasAttribute('data-fs-drop')) end(el);
    });
    let next = Infinity;
    document.querySelectorAll('[data-fs-drop-until]').forEach((el) => {
      const time = untilTime(el.getAttribute('data-fs-drop-until'));
      if (time !== null && time > now) next = Math.min(next, time);
    });
    if (timer) clearTimeout(timer);
    timer = null;
    // Fin dans les prochaines 24 h : programmée (au-delà, le prochain chargement s'en charge).
    if (Number.isFinite(next) && next - now < 86400000) timer = setTimeout(() => scan(document), Math.max(0, next - now) + 1000);
  };

  window.FSDrop = { active, scan };
  const start = () => { try { scan(); } catch (error) { console.warn('[Space Syndicate] fin de baisse indisponible', error); } };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true }); else start();
  document.addEventListener('shopify:section:load', (event) => scan(event.target || document));
}
